/*------------------------------------------------------------------------------
   About      : Edit-status and validation indicators (epic DA-697, II-FE-2)

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material'
import { CONTENT_EDIT_STATUS }   from 'types/cms/frontendTypes'
import { InsightValidationSummary,
         ValidationCheck,
         ValidationVerdict }     from '../utils/insightValidation'

/*------------------------------------------------------------------------------
   Edit status
------------------------------------------------------------------------------*/

/**
 * Shared by the list table's badge and the list header's count chips, so the
 * two can never disagree about what a Draft looks like.
 *
 * ⚠️ DRAFT is `info`, not `default`. A default Chip fills with `grey[300]`,
 * which on the list header sat on an almost identical page background and read
 * as an unstyled, possibly broken chip — the count next to it was legible only
 * because the text was.
 */
export const EDIT_STATUS_STYLE : Record<
  CONTENT_EDIT_STATUS,
  { label : string; color : 'info' | 'warning' | 'success' }
> = {
  [CONTENT_EDIT_STATUS.DRAFT]     : { label : 'Draft',     color : 'info' },
  [CONTENT_EDIT_STATUS.MODIFIED]  : { label : 'Modified',  color : 'warning' },
  [CONTENT_EDIT_STATUS.PUBLISHED] : { label : 'Published', color : 'success' },
}

export const InsightEditStatusBadge = ({ status }: { status : CONTENT_EDIT_STATUS }) => {
  const style = EDIT_STATUS_STYLE[status]
  // An unrecognised status still renders, showing the raw value — a blank cell
  // would read as "no status" rather than "a status this build does not know".
  if (!style) return <Chip label={status} size="small" variant="outlined" />

  return <Chip label={style.label} size="small" color={style.color} variant="filled" />
}

/*------------------------------------------------------------------------------
   Validation dots
------------------------------------------------------------------------------*/

const DOT_COLOR : Record<ValidationVerdict, string> = {
  PASS    : 'success.main',
  WARN    : 'warning.main',
  FAIL    : 'error.main',
  // Grey, not amber. Amber reads as "there is a problem"; these checks have
  // simply not run, which is a different thing and must look different.
  UNKNOWN : 'action.disabled',
}

const CHECK_LABEL : Record<ValidationCheck, string> = {
  variables : 'Variables',
  semantic  : 'Semantic',
  capping   : 'Capping',
}

/** Order matches the tiles on the edit screen so the two read the same. */
const CHECK_ORDER : ValidationCheck[] = ['variables', 'semantic', 'capping']

export const InsightValidationDots = ({ summary }: { summary : InsightValidationSummary }) => (
  <Tooltip
    placement="top"
    arrow
    title={
      <Stack spacing={0.25} sx={{ color : 'common.white' }}>
        {CHECK_ORDER.map((check) => (
          <Typography
            key={check}
            variant="caption"
            sx={{ display : 'block', color : 'common.white' }}
          >
            {/* Status only. The reasons live on the edit screen's tiles, where
                there is room to read them; here they made a wall of text over a
                row the user is only scanning. */}
            <strong>{CHECK_LABEL[check]}:</strong> {summary[check].verdict}
          </Typography>
        ))}
      </Stack>
    }
  >
    <Box sx={{ display : 'flex', gap : 0.5, alignItems : 'center' }}>
      {CHECK_ORDER.map((check) => (
        <Box
          key={check}
          sx={{
            width        : 8,
            height       : 8,
            borderRadius : '50%',
            bgcolor      : DOT_COLOR[summary[check].verdict],
            // An unrun check is hollow as well as grey, so the three states are
            // distinguishable without relying on colour alone.
            border       : summary[check].verdict === 'UNKNOWN' ? '1px solid' : 'none',
            borderColor  : 'action.disabled',
          }}
        />
      ))}
    </Box>
  </Tooltip>
)
